import { create } from 'zustand';
import type { User } from '@/types/api';

// Access token is held in memory ONLY (XSS-safe — no localStorage). The refresh
// token lives in an httpOnly cookie set by the backend. We cache the `user`
// object in localStorage so we can render immediately on boot, then re-verify
// via /auth/me.

const USER_KEY = 'sks_user';

function readCachedUser(): User | null {
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user: User | null) {
  if (user) window.localStorage.setItem(USER_KEY, JSON.stringify(user));
  else window.localStorage.removeItem(USER_KEY);
}

interface AuthState {
  accessToken: string | null;
  user: User | null;
  /** true until the initial /auth/me probe completes */
  bootstrapping: boolean;

  setSession: (accessToken: string, user: User) => void;
  setAccessToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  setBootstrapping: (b: boolean) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: readCachedUser(),
  bootstrapping: true,

  setSession: (accessToken, user) => {
    writeCachedUser(user);
    set({ accessToken, user, bootstrapping: false });
  },
  setAccessToken: (token) => set({ accessToken: token }),
  setUser: (user) => {
    writeCachedUser(user);
    set({ user });
  },
  setBootstrapping: (b) => set({ bootstrapping: b }),
  clear: () => {
    writeCachedUser(null);
    set({ accessToken: null, user: null, bootstrapping: false });
  },
}));

/** Synchronous getter for interceptors that can't subscribe. */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

export function setAccessToken(token: string | null): void {
  useAuthStore.getState().setAccessToken(token);
}

export function clearAuth(): void {
  useAuthStore.getState().clear();
}
